import { useSyncExternalStore } from 'react';
import { defaultsFromSchema, P1S } from '../settings/schema';
import { defaultSlots } from '../settings/filaments';
import {
  DEFAULT_FILAMENT_ID,
  DEFAULT_PRINTER_ID,
  DEFAULT_PROCESS_ID,
  findFilamentPreset,
  findProcessPreset,
  loadActivePresetIds,
  loadUserPresets,
  saveActivePresetIds,
  type Preset,
} from '../settings/presets';
import type { AppState, PlacedObject, ToolMode } from './types';
import { toast } from '../ui/toast';

type Listener = () => void;

const activeIds = loadActivePresetIds();
const bootProcess = findProcessPreset(activeIds.processId) || findProcessPreset(DEFAULT_PROCESS_ID)!;
const bootFilament = findFilamentPreset(activeIds.filamentId) || findFilamentPreset(DEFAULT_FILAMENT_ID)!;

let state: AppState = {
  objects: [],
  selectedId: null,
  settings: { ...defaultsFromSchema(), ...bootProcess.values, ...bootFilament.values },
  visibility: 'basic',
  tool: 'select',
  brushSize: 4,
  overhangHighlight: false,
  overhangAngle: 30,
  filaments: defaultSlots(),
  activeFilament: 0,
  projectName: 'Untitled',
  settingsOpen: false,
  settingsPanelVisible: true,
  objectsPanelVisible: true,
  toolbarExpanded: false,
  processPresetId: bootProcess.id,
  filamentPresetId: bootFilament.id,
  printerPresetId: activeIds.printerId || DEFAULT_PRINTER_ID,
  theme: 'light',
  viewMode: 'prepare',
  canUndo: false,
  canRedo: false,
};

const LS_THEME = 'p1s-slicer-theme';
try {
  const th = localStorage.getItem(LS_THEME);
  if (th === 'light' || th === 'dark' || th === 'auto') state.theme = th;
} catch { /* ignore */ }

const LS_PANELS = 'p1s-slicer-panels';
try {
  const raw = localStorage.getItem(LS_PANELS);
  if (raw) {
    const saved = JSON.parse(raw) as Partial<AppState>;
    if (typeof saved.settingsPanelVisible === 'boolean') state.settingsPanelVisible = saved.settingsPanelVisible;
    if (typeof saved.objectsPanelVisible === 'boolean') state.objectsPanelVisible = saved.objectsPanelVisible;
    if (typeof saved.toolbarExpanded === 'boolean') state.toolbarExpanded = saved.toolbarExpanded;
  }
} catch { /* ignore */ }

function persistPanels() {
  try {
    localStorage.setItem(
      LS_PANELS,
      JSON.stringify({
        settingsPanelVisible: state.settingsPanelVisible,
        objectsPanelVisible: state.objectsPanelVisible,
        toolbarExpanded: state.toolbarExpanded,
      })
    );
  } catch { /* ignore */ }
}

const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l();
}

const HISTORY_LIMIT = 30;

interface HistorySnap {
  label: string;
  objects: PlacedObject[];
  selectedId: string | null;
  settings: Record<string, string | number | boolean>;
  processPresetId: string;
  filamentPresetId: string;
  printerPresetId: string;
  projectName: string;
}

let past: HistorySnap[] = [];
let future: HistorySnap[] = [];
let recording = 0;
let gesture: HistorySnap | null = null;

function cloneObjects(objects: PlacedObject[]): PlacedObject[] {
  return objects.map((o) => ({
    ...o,
    position: [o.position[0], o.position[1], o.position[2]] as [number, number, number],
    rotation: [o.rotation[0], o.rotation[1], o.rotation[2]] as [number, number, number],
    scale: [o.scale[0], o.scale[1], o.scale[2]] as [number, number, number],
    overrides: { ...o.overrides },
    paintSupports: o.paintSupports ? new Uint8Array(o.paintSupports) : undefined,
  }));
}

function takeSnap(label: string): HistorySnap {
  return {
    label,
    objects: cloneObjects(state.objects),
    selectedId: state.selectedId,
    settings: { ...state.settings },
    processPresetId: state.processPresetId,
    filamentPresetId: state.filamentPresetId,
    printerPresetId: state.printerPresetId,
    projectName: state.projectName,
  };
}

function applySnap(snap: HistorySnap) {
  setState({
    objects: cloneObjects(snap.objects),
    selectedId:
      snap.selectedId && snap.objects.some((o) => o.id === snap.selectedId)
        ? snap.selectedId
        : (snap.objects[0]?.id ?? null),
    settings: { ...snap.settings },
    processPresetId: snap.processPresetId,
    filamentPresetId: snap.filamentPresetId,
    printerPresetId: snap.printerPresetId,
    projectName: snap.projectName,
  });
}

function paintSig(p?: Uint8Array) {
  if (!p || !p.length) return '0';
  let h = p.length;
  for (let i = 0; i < p.length; i++) h = (Math.imul(h, 33) + p[i]) | 0;
  return `${p.length}:${h}`;
}

function sameScene(snap: HistorySnap): boolean {
  if (snap.selectedId !== state.selectedId) return false;
  if (snap.processPresetId !== state.processPresetId) return false;
  if (snap.filamentPresetId !== state.filamentPresetId) return false;
  if (snap.printerPresetId !== state.printerPresetId) return false;
  if (snap.projectName !== state.projectName) return false;
  if (JSON.stringify(snap.settings) !== JSON.stringify(state.settings)) return false;
  if (snap.objects.length !== state.objects.length) return false;
  for (let i = 0; i < snap.objects.length; i++) {
    const a = snap.objects[i];
    const b = state.objects[i];
    if (a.id !== b.id || a.name !== b.name) return false;
    if (a.position.some((v, j) => v !== b.position[j])) return false;
    if (a.rotation.some((v, j) => v !== b.rotation[j])) return false;
    if (a.scale.some((v, j) => v !== b.scale[j])) return false;
    if (JSON.stringify(a.overrides) !== JSON.stringify(b.overrides)) return false;
    if (paintSig(a.paintSupports) !== paintSig(b.paintSupports)) return false;
  }
  return true;
}

function pushPast(snap: HistorySnap) {
  past.push(snap);
  if (past.length > HISTORY_LIMIT) past.shift();
  future = [];
}

function settingLabel(id: string) {
  const names: Record<string, string> = {
    sparse_infill_density: 'Infill',
    enable_support: 'Support',
    brim_type: 'Adhesion',
    layer_height: 'Layer height',
    wall_loops: 'Walls',
  };
  if (names[id]) return names[id];
  return id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function setState(partial: Partial<AppState> | ((s: AppState) => Partial<AppState>)) {
  const patch = typeof partial === 'function' ? partial(state) : partial;
  state = { ...state, ...patch, canUndo: past.length > 0, canRedo: future.length > 0 };
  if (
    'settingsPanelVisible' in patch ||
    'objectsPanelVisible' in patch ||
    'toolbarExpanded' in patch
  ) {
    persistPanels();
  }
  if (
    'processPresetId' in patch ||
    'filamentPresetId' in patch ||
    'printerPresetId' in patch
  ) {
    saveActivePresetIds({
      processId: state.processPresetId,
      filamentId: state.filamentPresetId,
      printerId: state.printerPresetId,
    });
  }
  if ('theme' in patch) {
    try {
      localStorage.setItem(LS_THEME, state.theme);
    } catch { /* ignore */ }
  }
  emit();
}

function uid(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

function bboxOf(positions: Float32Array, scale: [number, number, number]) {
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  const [sx, sy, sz] = scale;
  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i] * sx;
    const y = positions[i + 1] * sy;
    const z = positions[i + 2] * sz;
    minX = Math.min(minX, x); maxX = Math.max(maxX, x);
    minY = Math.min(minY, y); maxY = Math.max(maxY, y);
    minZ = Math.min(minZ, z); maxZ = Math.max(maxZ, z);
  }
  return { minX, minY, minZ, maxX, maxY, maxZ, sx: maxX - minX, sy: maxY - minY, sz: maxZ - minZ };
}

export const store = {
  getSnapshot: () => state,
  subscribe: (l: Listener) => {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  set: setState,

  record(label: string, fn: () => void) {
    if (recording > 0) {
      fn();
      return;
    }
    const before = takeSnap(label);
    recording += 1;
    try {
      pushPast(before);
      fn();
    } finally {
      recording -= 1;
    }
  },

  beginGesture(label: string) {
    if (gesture || recording > 0) return;
    gesture = takeSnap(label);
  },

  endGesture() {
    if (!gesture) return;
    const snap = gesture;
    gesture = null;
    if (sameScene(snap)) return;
    pushPast(snap);
    setState({});
  },

  undo() {
    if (!past.length) return;
    const before = past.pop()!;
    future.push(takeSnap(before.label));
    applySnap(before);
    toast(`Undid: ${before.label}`);
  },

  redo() {
    if (!future.length) return;
    const after = future.pop()!;
    past.push(takeSnap(after.label));
    if (past.length > HISTORY_LIMIT) past.shift();
    applySnap(after);
    toast(`Redid: ${after.label}`);
  },

  addMesh(name: string, positions: Float32Array) {
    const id = uid('obj');
    const scale: [number, number, number] = [1, 1, 1];
    // Normalise mesh: centre XZ on origin, drop base to Y=0 (Y-up)
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (let i = 0; i < positions.length; i += 3) {
      minX = Math.min(minX, positions[i]); maxX = Math.max(maxX, positions[i]);
      minY = Math.min(minY, positions[i + 1]); maxY = Math.max(maxY, positions[i + 1]);
      minZ = Math.min(minZ, positions[i + 2]); maxZ = Math.max(maxZ, positions[i + 2]);
    }
    const cx = (minX + maxX) / 2;
    const cz = (minZ + maxZ) / 2;
    const normalised = new Float32Array(positions.length);
    for (let i = 0; i < positions.length; i += 3) {
      normalised[i] = positions[i] - cx;
      normalised[i + 1] = positions[i + 1] - minY;
      normalised[i + 2] = positions[i + 2] - cz;
    }
    const obj: PlacedObject = {
      id,
      name,
      positions: normalised,
      position: [P1S.bedX / 2, P1S.bedY / 2, 0],
      rotation: [0, 0, 0],
      scale,
      overrides: {},
      extruder: state.activeFilament,
      paintSupports: new Uint8Array(normalised.length / 9),
    };
    store.record('Add object', () => {
      setState({
        objects: [...state.objects, obj],
        selectedId: id,
        projectName:
          state.objects.length === 0 && state.projectName === 'Untitled' ? name : state.projectName,
      });
    });
    return id;
  },

  select(id: string | null) {
    setState({ selectedId: id });
  },

  updateObject(id: string, patch: Partial<PlacedObject>) {
    setState({
      objects: state.objects.map((o) => (o.id === id ? { ...o, ...patch } : o)),
    });
  },

  deleteSelected() {
    if (!state.selectedId) return;
    store.record('Delete', () => {
      const objects = state.objects.filter((o) => o.id !== state.selectedId);
      setState({ objects, selectedId: objects[0]?.id ?? null });
    });
  },

  duplicateSelected() {
    const src = state.objects.find((o) => o.id === state.selectedId);
    if (!src) return;
    const copy: PlacedObject = {
      ...src,
      id: uid('obj'),
      name: `${src.name} copy`,
      position: [src.position[0] + 15, src.position[1] + 15, src.position[2]],
      overrides: { ...src.overrides },
      paintSupports: src.paintSupports ? new Uint8Array(src.paintSupports) : undefined,
      positions: src.positions, // share geometry buffer
    };
    store.record('Duplicate', () => {
      setState({ objects: [...state.objects, copy], selectedId: copy.id });
    });
  },

  setSetting(id: string, value: string | number | boolean) {
    store.record(settingLabel(id), () => {
      setState({ settings: { ...state.settings, [id]: value } });
    });
  },

  setSettings(values: Record<string, string | number | boolean>) {
    store.record('Settings', () => {
      setState({ settings: { ...values } });
    });
  },

  setVisibility(visibility: AppState['visibility']) {
    setState({ visibility });
  },

  setTool(tool: ToolMode) {
    setState({ tool });
  },

  dropSelectedToBed() {
    const apply = () => {
      const o = state.objects.find((x) => x.id === state.selectedId);
      if (!o) return;
      const bb = bboxOf(o.positions, o.scale);
      store.updateObject(o.id, {
        position: [o.position[0], o.position[1], -bb.minY],
      });
    };
    if (recording > 0) apply();
    else store.record('Drop', apply);
  },

  centreSelected() {
    store.record('Centre', () => {
      const o = state.objects.find((x) => x.id === state.selectedId);
      if (!o) return;
      store.updateObject(o.id, {
        position: [P1S.bedX / 2, P1S.bedY / 2, o.position[2]],
      });
    });
  },

  autoArrange() {
    const gap = 8;
    let x = 20;
    let y = 20;
    let rowH = 0;
    const next = state.objects.map((o) => {
      const bb = bboxOf(o.positions, o.scale);
      if (x + bb.sx > P1S.bedX - 10) {
        x = 20;
        y += rowH + gap;
        rowH = 0;
      }
      const pos: [number, number, number] = [x + bb.sx / 2, y + bb.sz / 2, -bb.minY];
      x += bb.sx + gap;
      rowH = Math.max(rowH, bb.sz);
      return { ...o, position: pos };
    });
    store.record('Arrange', () => {
      setState({ objects: next });
    });
  },

  setObjectOverride(id: string, key: string, value: string | number | boolean | undefined) {
    store.record('Object override', () => {
    setState({
      objects: state.objects.map((o) => {
        if (o.id !== id) return o;
        const overrides = { ...o.overrides };
        if (value === undefined) delete overrides[key];
        else overrides[key] = value;
        return { ...o, overrides };
      }),
    });
    });
  },


  applyProcessPreset(preset: Preset) {
    store.record('Process preset', () => {
      setState({
        processPresetId: preset.id,
        settings: { ...state.settings, ...preset.values },
      });
    });
  },

  applyFilamentPreset(preset: Preset) {
    store.record('Filament preset', () => {
      setState({
        filamentPresetId: preset.id,
        settings: { ...state.settings, ...preset.values },
      });
    });
  },

  applyPrinterPreset(preset: Preset) {
    store.record('Printer preset', () => {
      setState({ printerPresetId: preset.id });
    });
  },

  resetSettingToProcess(key: string) {
    const p = findProcessPreset(state.processPresetId, loadUserPresets());
    if (!p || !(key in p.values)) return;
    store.record(settingLabel(key), () => {
      setState({ settings: { ...state.settings, [key]: p.values[key] } });
    });
  },

  resetSettingToFilament(key: string) {
    const p = findFilamentPreset(state.filamentPresetId, loadUserPresets());
    if (!p || !(key in p.values)) return;
    store.record(settingLabel(key), () => {
      setState({ settings: { ...state.settings, [key]: p.values[key] } });
    });
  },

  paintTriangles(objectId: string, triangleIndices: number[], value: 0 | 1 | 2) {
    setState({
      objects: state.objects.map((o) => {
        if (o.id !== objectId) return o;
        const triCount = o.positions.length / 9;
        const paint = o.paintSupports
          ? new Uint8Array(o.paintSupports)
          : new Uint8Array(triCount);
        for (const ti of triangleIndices) {
          if (ti >= 0 && ti < paint.length) paint[ti] = value;
        }
        return { ...o, paintSupports: paint };
      }),
    });
  },
};

export function useAppStore<T>(selector: (s: AppState) => T): T {
  return useSyncExternalStore(
    store.subscribe,
    () => selector(store.getSnapshot()),
    () => selector(store.getSnapshot())
  );
}

export function objectBounds(o: PlacedObject) {
  return bboxOf(o.positions, o.scale);
}

export function isOutOfBounds(o: PlacedObject): boolean {
  const bb = bboxOf(o.positions, o.scale);
  const halfX = bb.sx / 2;
  const halfY = bb.sz / 2;
  const x0 = o.position[0] - halfX;
  const x1 = o.position[0] + halfX;
  const y0 = o.position[1] - halfY;
  const y1 = o.position[1] + halfY;
  return x0 < 0 || y0 < 0 || x1 > P1S.bedX || y1 > P1S.bedY || bb.sy > P1S.bedZ;
}
